// S15-1/2/3: rider job-alert audio.
//   S15-1 fix: volume is user-configurable via localStorage 'gxl-rider-alarm-volume'
//     (0–1 float). Existing `playJobAlert()`/`startJobAlertLoop()` callers don't
//     need to change — they read the setting per invocation.
//   S15-2 fix: alarm oscillators that were scheduled but hadn't fired yet
//     used to keep beeping for up to ~1s after `stopJobAlertLoop()` was called.
//     Fixed by tracking live oscillators in a Set and calling `.stop(0)` on each
//     during stop. Prevents the "cancel then hear one more beep" ghost.
//   S15-3 fix: envelope was ramp-up 10ms then exponential decay over the whole
//     duration — the tail was inaudible on a moving bike. Now: ramp up 10ms,
//     hold at volume for 80% of duration, then decay in the last 20%. Same
//     total duration; the audible portion is loud throughout.

let ctx: AudioContext | null = null
const liveOscillators = new Set<OscillatorNode>()

const VOLUME_KEY = 'gxl-rider-alarm-volume'
const DEFAULT_VOLUME = 0.7  // Louder default than the old 0.35 — a moving bike is noisy

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctx) return ctx
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  if (!AC) return null
  ctx = new AC()
  return ctx
}

export function primeAudio(): void {
  const c = getContext()
  if (c && c.state === 'suspended') void c.resume()
}

/** Returns true if the AudioContext is running (i.e. a user gesture has unlocked it). */
export function isAudioUnlocked(): boolean {
  const c = getContext()
  return c?.state === 'running'
}

/** User-configurable alarm volume (0.0 – 1.0). Persisted in localStorage. */
export function getAlarmVolume(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_VOLUME
  const raw = localStorage.getItem(VOLUME_KEY)
  if (!raw) return DEFAULT_VOLUME
  const parsed = parseFloat(raw)
  if (Number.isNaN(parsed)) return DEFAULT_VOLUME
  return Math.max(0, Math.min(1, parsed))
}

export function setAlarmVolume(v: number): void {
  if (typeof localStorage === 'undefined') return
  const clamped = Math.max(0, Math.min(1, v))
  localStorage.setItem(VOLUME_KEY, clamped.toString())
}

// baseVolume is the tone's own dynamic weight (0.5, 0.6, etc. below) so the
// three beeps can be relatively louder/quieter. Multiplied by the user-set
// master volume to produce the final gain.
function beep(freq: number, durationMs: number, startOffsetMs: number, baseVolume: number): void {
  const c = getContext()
  if (!c) return
  const master = getAlarmVolume()
  if (master === 0) return  // muted — don't schedule the oscillator at all

  const t0 = c.currentTime + startOffsetMs / 1000
  const durationS = durationMs / 1000
  const t1 = t0 + durationS
  const holdUntil = t0 + durationS * 0.8  // S15-3: sustain 80% then fade the last 20%
  const peakGain = baseVolume * master

  const osc = c.createOscillator()
  const gain = c.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(freq, t0)
  gain.gain.setValueAtTime(0, t0)
  gain.gain.linearRampToValueAtTime(peakGain, t0 + 0.01)  // 10ms attack
  gain.gain.setValueAtTime(peakGain, holdUntil)           // hold
  gain.gain.exponentialRampToValueAtTime(0.0001, t1)      // short decay tail
  osc.connect(gain).connect(c.destination)
  osc.start(t0)
  osc.stop(t1)

  // S15-2: track this oscillator so a subsequent stopJobAlertLoop() can kill it
  // synchronously instead of letting it play out its scheduled envelope.
  liveOscillators.add(osc)
  osc.onended = () => { liveOscillators.delete(osc) }
}

/** Distinct three-pulse alert for incoming job — hard to miss on a bike. */
export function playJobAlert(): void {
  beep(1047, 180,   0, 0.5)
  beep(1047, 180, 220, 0.5)
  beep(1319, 280, 440, 0.6)
}

let alertLoop: ReturnType<typeof setInterval> | null = null

/** Plays the alert immediately then repeats every 4 s until stopJobAlertLoop() is called. */
export function startJobAlertLoop(): void {
  stopJobAlertLoop()
  playJobAlert()
  alertLoop = setInterval(playJobAlert, 4000)
}

export function stopJobAlertLoop(): void {
  if (alertLoop !== null) {
    clearInterval(alertLoop)
    alertLoop = null
  }
  // S15-2: kill every oscillator that was scheduled to play but hasn't yet
  // (or is playing right now). Without this, the next 1–2 seconds of already-
  // scheduled beeps would fire after the user tapped cancel.
  for (const osc of liveOscillators) {
    try { osc.stop(0) } catch { /* already stopped */ }
  }
  liveOscillators.clear()
}
